// 二级单「权益明细」动态表单：schema 类型 + 值转换 + attr→SFormItem 映射
// 对齐 handleColumns / CooperationPropertyForm 的动态字段体系：
//   - attributes.required → required_mode（0/1/2/3）+ required_condition（jsonLogic 条件联动）
//   - options 仅 ConditionOptionsData 形态（use_condition + groups）
//   - 值模型：value 恒为 string[]，单值取首元素；dropdown_list/date_range 保数组
import dayjs from 'dayjs';
import {
  enums,
  type IpBenefitValueDto,
  type ConditionOptionsData,
} from '#services/api';
import type { SFormComponentClass } from '@mpc/common/src/components/SForm/SFormElement';
import {
  getRequiredRule,
  getOptionsRule,
  getLogicOptions,
  getConditionDisabled,
} from '@mpc/common/src/features/utils';

/** attributes 控件类型（对齐 DemandCooperatePropertyTypeEnum 子集） */
export const BENEFIT_ATTR_TYPE = {
  NUMERIC: 'numeric',
  INPUT: 'input',
  TEXT: 'text',
  DROPDOWN_LIST: 'dropdown_list',
  DROPDOWN_RADIO: 'dropdown_radio',
  DATE_RANGE: 'date_range',
  RADIO: 'radio',
} as const;
export type BenefitAttrType =
  (typeof BENEFIT_ATTR_TYPE)[keyof typeof BENEFIT_ATTR_TYPE];

/** 多值类型：表单值保持数组（对齐 CooperationPropertyForm modifyDataModel） */
const MULTI_VALUE_TYPES: BenefitAttrType[] = [
  BENEFIT_ATTR_TYPE.DROPDOWN_LIST,
  BENEFIT_ATTR_TYPE.DATE_RANGE,
];

function isMultiValueType(type: BenefitAttrType): boolean {
  return MULTI_VALUE_TYPES.includes(type);
}

/** 单个 attribute schema（attributes.required 已替换为 required_mode，对齐 PropertyRefDto） */
export interface BenefitAttrDef {
  attr_code: string;
  attr_name: string;
  type: BenefitAttrType;
  required_mode?: 0 | 1 | 2 | 3;
  required_condition?: { express?: string; fallback?: 0 | 1 | 2 | 3 };
  max_length?: number;
  options?: ConditionOptionsData;
}

/** 权益分组定义（useDemandOptions ip_cooperation_benefit 的每个条目） */
export interface BenefitGroupDef {
  name: string;
  /** 分组标识（useDemandOptions ip_cooperation_benefit 实际返回 code，历史契约误用 value） */
  code: string;
  group?: string;
  /** 权益定义版本号（后端 IpBenefitOptionVO.version；随 template 序列化持久化，详情回填时对比是否过期） */
  version?: string;
  /** 分组级必填模式（对齐 attribute 的 required_mode：0=非必填，1=必填，2=条件必填，3=不可填） */
  required_mode?: number;
  /** 兼容历史契约：布尔必填（部分数据源仍返回 required） */
  required?: boolean;
  attributes?: BenefitAttrDef[];
}

/** 权益明细单行数据（每行 = 一个权益分组），供受控表格行渲染与 buildRowsFromBenefits 回填 */
export interface BenefitRowData {
  /** 权益分组 value */
  group_value: string;
  /** 权益分组名称 */
  group_name: string;
  /** 是否开启（开关列字段） */
  enabled: boolean;
  /** 是否必要分组（开关列 disabled 判定） */
  required: boolean;
  /** 该分组 attribute schema（用于渲染权益说明列） */
  attributes?: BenefitAttrDef[];
  /** 权益定义序列化快照（buildRowsFromBenefits 按状态回填：方案设计/版本匹配取 options 最新，非方案设计+版本过期取 detail 历史；提交经 rowsToBenefits 回传） */
  template?: string;
  /** 动态 attribute 字段值（attr_code → 表单值） */
  [attrCode: string]: any;
}

/** 分组是否必填：分组级 required_mode===1 判定（兼容 required 布尔历史契约） */
export function isBenefitGroupRequired(
  g: Pick<BenefitGroupDef, 'required_mode' | 'required'>
): boolean {
  return g.required_mode === 1 || g.required === true;
}

/** date_range 值转换：外部字符串数组 ↔ 控件 dayjs 数组 */
const DATE_RANGE_VALUE_FORMAT = {
  valueToField: (value: string[] | undefined) => {
    if (!Array.isArray(value) || value.length < 2) return undefined;
    const start = dayjs(value[0]);
    const end = dayjs(value[1]);
    if (!start.isValid() || !end.isValid()) return undefined;
    return [start, end];
  },
  fieldToValue: (fieldValue: any) => {
    if (
      !Array.isArray(fieldValue) ||
      fieldValue.length < 2 ||
      !fieldValue[0] ||
      !fieldValue[1]
    ) {
      return undefined;
    }
    return [
      dayjs(fieldValue[0]).format('YYYY-MM-DD'),
      dayjs(fieldValue[1]).format('YYYY-MM-DD'),
    ];
  },
};

/** 后端 schema 字段名差异适配（utils 复用函数读 code/data_type，attribute 用 attr_code/type） */
function toColumnLike(attr: BenefitAttrDef) {
  return {
    ...attr,
    code: attr.attr_code,
    data_type: attr.type,
  };
}

export interface AttrSFormProps {
  componentClass: SFormComponentClass;
  col: number;
  rules: any[];
  elementProps: any;
  valueFormat?: { valueToField: any; fieldToValue: any };
}

/**
 * attribute → SFormItem 配置。
 * 复用 getRequiredRule / getOptionsRule / getLogicOptions / getConditionDisabled：
 *  - required_mode 0/1/2/3 语义（必填 / 条件必填 / 不可填）
 *  - required_condition.express 用当前 formData 求值 → require / disable 联动
 *  - options.use_condition + groups 条件选项 → getLogicOptions
 * formData 变化由外部 useSForm state 驱动重渲染 → 本函数每次渲染重算
 */
export function attrToSFormProps(
  attr: BenefitAttrDef,
  formValues?: Record<string, any>
): AttrSFormProps {
  const row = formValues ?? {};
  const requireRule = getRequiredRule(toColumnLike(attr), row);
  const conditionDisabled = getConditionDisabled(toColumnLike(attr), row);
  const options = getLogicOptions(toColumnLike(attr), row);

  const base = { rules: [...requireRule] };
  const disabled = conditionDisabled ? true : undefined;

  switch (attr.type) {
    case BENEFIT_ATTR_TYPE.NUMERIC:
      return {
        ...base,
        componentClass: 'inputNumber',
        col: 24,
        elementProps: {
          placeholder: '请输入',
          precision: attr?.precision ?? 2,
          toFixed: attr?.precision ?? 2,
        },
      };
    case BENEFIT_ATTR_TYPE.INPUT:
      return {
        ...base,
        componentClass: 'input',
        col: 24,
        elementProps: {
          placeholder: '请输入',
          maxLength: attr.max_length ?? 50,
          disabled,
        },
      };
    case BENEFIT_ATTR_TYPE.TEXT:
      return {
        ...base,
        componentClass: 'textarea',
        col: 24,
        elementProps: {
          placeholder: '请输入',
          maxLength: attr.max_length ?? 500,
          showCount: true,
          rows: 2,
          disabled,
        },
      };
    case BENEFIT_ATTR_TYPE.DROPDOWN_LIST:
      return {
        ...base,
        componentClass: 'select',
        col: 24,
        rules: [...requireRule, ...getOptionsRule(options)],
        elementProps: {
          multiple: true,
          allowClear: true,
          placeholder: '请选择',
          showSearch: true,
          options,
          disabled,
        },
      };
    case BENEFIT_ATTR_TYPE.DROPDOWN_RADIO:
      return {
        ...base,
        componentClass: 'select',
        col: 24,
        rules: [...requireRule, ...getOptionsRule(options)],
        elementProps: {
          multiple: false,
          allowClear: true,
          placeholder: '请选择',
          showSearch: true,
          options,
          disabled,
        },
      };
    case BENEFIT_ATTR_TYPE.RADIO:
      return {
        ...base,
        componentClass: 'radio',
        col: 24,
        rules: [...requireRule, ...getOptionsRule(options)],
        elementProps: {
          options,
          disabled,
        },
      };
    case BENEFIT_ATTR_TYPE.DATE_RANGE:
      return {
        ...base,
        componentClass: 'datePicker',
        col: 24,
        elementProps: {
          range: true,
          placeholder: ['开始日期', '结束日期'],
          disabled,
        },
        valueFormat: DATE_RANGE_VALUE_FORMAT,
      };
    default:
      return {
        ...base,
        componentClass: 'input',
        col: 24,
        elementProps: { placeholder: '请输入' },
      };
  }
}

/** 后端 value: string[] → 表单控件值（多值保数组，单值取首元素；numeric 转 number） */
function formValueFromAttrValue(attr: BenefitAttrDef, value: string[]): any {
  if (isMultiValueType(attr.type)) return value;
  const first = value[0];
  if (attr.type === BENEFIT_ATTR_TYPE.NUMERIC) {
    const n = Number(first);
    return Number.isNaN(n) ? undefined : n;
  }
  return first;
}

/**
 * benefits 数组 → 表单扁平值 + 已勾选可选分组。
 * checked = benefits 中存在的可选分组（加载回填 checkbox 勾选态）
 */
export function benefitsToFlat(
  benefits: IpBenefitValueDto[] | undefined | null,
  optionDefs: BenefitGroupDef[]
): { flat: Record<string, any>; checked: string[] } {
  const flat: Record<string, any> = {};
  const checked: string[] = [];
  for (const def of optionDefs) {
    const group = benefits?.find((b) => b.benefit_code === def.code);
    if (!group) continue;
    if (!isBenefitGroupRequired(def)) checked.push(def.code);
    for (const attr of def.attributes ?? []) {
      const attrValue = group.values?.find(
        (v) => v.attr_code === attr.attr_code
      );
      if (!attrValue?.value) continue;
      flat[attr.attr_code] = formValueFromAttrValue(attr, attrValue.value);
    }
  }
  return { flat, checked };
}

/**
 * 从权益模板（template 序列化快照）解析保存时的定义版本。
 * template 缺失 / JSON 损坏 / 历史数据无 version → 返回 undefined（跳过版本对比，原样回填）。
 */
export function parseBenefitTemplateVersion(
  template?: string
): string | undefined {
  if (!template) return undefined;
  try {
    const parsed = JSON.parse(template) as { version?: string } | null;
    return parsed?.version;
  } catch {
    return undefined;
  }
}

/**
 * 从权益模板（template 序列化快照）解析 schema 定义（name/group/version/required/attributes）。
 * 非方案设计节点 + 版本过期时，行渲染 schema 与提交 template 均回退到 detail 保存的历史快照
 * （对齐外包 DetailContent 用 template 解析 options 的范式）；template 缺失 / JSON 损坏 → undefined。
 */
export function parseBenefitTemplateSchema(
  template?: string
): Partial<BenefitGroupDef> | undefined {
  if (!template) return undefined;
  try {
    const parsed = JSON.parse(template) as {
      name?: string;
      group?: string;
      version?: string;
      required?: boolean;
      attributes?: BenefitAttrDef[];
    } | null;
    if (!parsed) return undefined;
    return {
      name: parsed.name,
      group: parsed.group,
      version: parsed.version,
      // 对齐外包 DetailContent 契约：template 布尔 required → required_mode 0/1
      required_mode: parsed.required ? 1 : 0,
      required: parsed.required,
      attributes: parsed.attributes,
    };
  } catch {
    return undefined;
  }
}

/**
 * 从 benefits 回填 rows：required 默认开启；optional 仅在已存在时开启。
 * 详情回填统一经此纯函数（config 层），组件侧只负责触发时机与 setFieldsValue。
 * 版本对比（detail 权益保存的 version 自 template 解析）与 options 当前 version 不一致时按二级单状态分流：
 *   - 方案设计节点（L2_OFFER_DESIGN）：定义已更新，清空用户旧输入（仅 attr 值，enabled 勾选态保留），按最新 options schema 渲染；
 *   - 非方案设计节点（审批/谈判/进行中）：保留历史值展示，从 detail benefits 回填字段 + 历史 template（行 schema 同步用历史快照），不清空；
 *     detail 未返回的权益（disable 可选组未开启）enabled=false，attributes 沿用 options 最新 schema。
 * template 缺失 / 历史数据无 version → 跳过版本对比，原样回填。
 */
export function buildRowsFromBenefits(
  benefits: IpBenefitValueDto[] | null | undefined,
  options: BenefitGroupDef[],
  stateCode?: string
): BenefitRowData[] {
  const savedList = benefits ?? [];
  // 仅取 checked 勾选态（可选分组是否已勾选）；attr 值回填改为按行级 schema 从 detail values 直接匹配
  const { checked } = benefitsToFlat(savedList, options);
  const isDesignNode =
    stateCode === enums.IpCooperationL2StateEnum.L2_OFFER_DESIGN;
  return options.map((g) => {
    const saved = savedList.find((b) => b.benefit_code === g.code);
    const savedVersion = saved && parseBenefitTemplateVersion(saved.template);
    const stale = savedVersion !== undefined && savedVersion !== g.version;
    // 非方案设计 + 版本过期：行 schema/template 回退 detail 保存的历史快照（保留历史值展示）；否则 options 最新
    const useDetailSchema = !isDesignNode && stale && !!saved?.template;
    const schemaDef = useDetailSchema
      ? (parseBenefitTemplateSchema(saved.template) ?? g)
      : g;
    const isEnabled = isBenefitGroupRequired(g) || checked.includes(g.code);
    const row: BenefitRowData = {
      group_value: g.code,
      group_name: g.name,
      enabled: isEnabled,
      required: isBenefitGroupRequired(g),
      attributes: schemaDef.attributes,
      // 提交经 rowsToBenefits 回传：非方案设计+过期保留 detail 历史 template，否则 options 最新
      template: useDetailSchema ? saved.template : serializeBenefitTemplate(g),
    };
    // 关闭行不写入 attr 值，避免误提交；方案设计 + 版本过期清空旧输入（enabled 保留），其余开启行按行级 schema 回填
    if (isEnabled && !(isDesignNode && stale)) {
      for (const attr of row.attributes ?? []) {
        const attrValue = saved?.values?.find(
          (v) => v.attr_code === attr.attr_code
        );
        if (!attrValue?.value) continue;
        row[attr.attr_code] = formValueFromAttrValue(attr, attrValue.value);
      }
    }
    return row;
  });
}

/** 表单控件值 → 后端 value: string[]（空值返回 undefined 跳过） */
function attrValueFromFormValue(
  attr: BenefitAttrDef,
  formValue: any
): string[] | undefined {
  if (formValue === undefined || formValue === null) return undefined;
  if (isMultiValueType(attr.type)) {
    if (!Array.isArray(formValue) || !formValue.length) return undefined;
    return formValue.map((v: any) => String(v));
  }
  const s = String(formValue);
  if (s === '' || s === 'undefined' || s === 'NaN') return undefined;
  return [s];
}

/** RowData 最小契约（避免 BenefitConfigNew 与 config.ts 相互依赖循环） */
export interface BenefitRowLike {
  group_value: string;
  enabled?: boolean;
  [attrCode: string]: any;
}

/**
 * 将未开启权益行置为开启（offer 谈判「新增权益」按钮场景）。
 * 行数据是全量权益列表（buildRowsFromBenefits 已含 enabled=false 行），不 push 新行——
 * 仅把 codes 命中的已有行 enabled=true 并从原位置移除、按 codes 顺序追加到数组末尾（「新增」视觉在表格尾部展示），
 * template 刷新为 options 最新 schema（覆盖未开启行可能残留的 detail 历史/旧快照，新增配置用最新定义）。
 * codes 未命中现有行的 code（options 新增权益但行数据缺行）防御性按 options 补建开启行。
 */
export function enableBenefitRows(
  rows: BenefitRowData[] | undefined | null,
  codes: string[],
  optionDefs: BenefitGroupDef[]
): BenefitRowData[] {
  const base = rows ?? [];
  if (!codes?.length) return base;
  const codeSet = new Set(codes);
  // 未命中行保持原顺序；命中行置为开启 + 刷新 template，统一按 codes 顺序追加到数组末尾
  const otherRows = base.filter((r) => !codeSet.has(r.group_value));
  const enabledRows = codes
    .map((c) => {
      const row = base.find((r) => r.group_value === c);
      if (!row) return null;
      const def = optionDefs.find((o) => o.code === c);
      return {
        ...row,
        enabled: true,
        // 刷新为 options 最新 schema 的 template（新增配置用最新定义，覆盖旧快照）
        template: def ? serializeBenefitTemplate(def) : row.template,
      };
    })
    .filter(Boolean) as BenefitRowData[];
  const next = [...otherRows, ...enabledRows];
  // 防御：codes 未命中现有行（options 新增权益但行数据缺行）→ 按 options 补建开启行
  const existingCodes = new Set(base.map((r) => r.group_value));
  for (const c of codes) {
    if (existingCodes.has(c)) continue;
    const def = optionDefs.find((o) => o.code === c);
    if (!def) continue;
    next.push({
      group_value: def.code,
      group_name: def.name,
      enabled: true,
      required: isBenefitGroupRequired(def),
      attributes: def.attributes,
      template: serializeBenefitTemplate(def),
    });
  }
  return next;
}

/**
 * 条件必填「不可填」清空：required_mode===2 且 required_condition.fallback===3 时，
 * 联动求值 required=false（条件不满足 → fallback=3 不可填）→ 该 attr 已填数据置 null 清空。
 * 复用 getConditionDisabled（true ⟺ 不可填态 required===3）判定；无变化返回 null。
 * 清空用 null 而非 undefined（SForm wrapperToValue 的 isUndefined 会把 undefined 回退到旧值残留）。
 */
export function clearNotFillableRows(
  rows: BenefitRowData[] | undefined | null,
  formValues: Record<string, any>
): BenefitRowData[] | null {
  if (!rows?.length) return null;
  let changed = false;
  const next = rows.map((row) => {
    const attrs = row.attributes ?? [];
    if (!attrs.length) return row;
    let rowChanged = false;
    const newRow = { ...row };
    for (const attr of attrs) {
      // 仅条件必填 + fallback=3 参与：其余 required_mode 无「不可填」态，不动
      if (attr.required_mode !== 2 || attr.required_condition?.fallback !== 3)
        continue;
      if (!getConditionDisabled(toColumnLike(attr), formValues)) continue; // 条件满足 → 仍必填，不清空
      if (newRow[attr.attr_code] == null) continue; // 无数据无需清空
      newRow[attr.attr_code] = null; // 用 null 清空（undefined 会被 SForm 旧值回退覆盖）
      rowChanged = true;
    }
    if (rowChanged) changed = true;
    return newRow;
  });
  return changed ? next : null;
}

/**
 * 序列化单个权益分组定义为 template 字符串（提交时回传后端，便于后端校验/历史回放）
 * 仅保留可序列化字段，过滤循环引用与函数
 * version 一并序列化：详情回填时据此对比 options 最新版本，判定权益定义是否已更新
 */
export function serializeBenefitTemplate(def: BenefitGroupDef): string {
  const safe = {
    name: def.name,
    group: def.group,
    version: def.version,
    required: isBenefitGroupRequired(def),
    attributes: (def.attributes ?? []).map((attr) => ({
      attr_code: attr.attr_code,
      attr_name: attr.attr_name,
      type: attr.type,
      required_mode: attr.required_mode,
      required_condition: attr.required_condition,
      max_length: attr.max_length,
      options: attr.options,
    })),
  };
  return JSON.stringify(safe);
}

/**
 * 受控行数据 → benefits 提交数组（纯遍历，无 optionDefs 匹配）。
 * rows = BenefitConfigNew 的 value/onChange 数组，已是 options 全量权益列表（每行自含 attributes/template/required/enabled）
 * 仅提交 enabled 行（required 组恒开 enabled=true，严格等价旧「必要组 + 已勾选可选组」语义）；空 attr 值的组跳过
 * template 优先取行携带值（buildRowsFromBenefits 按状态回填：非方案设计+过期 = detail 历史 / 其余 = options 最新），
 * 缺省序列化行自身（对齐后端 IpBenefitValueDto.template）
 */
export function rowsToBenefits(
  rows: BenefitRowLike[] | undefined | null
): IpBenefitValueDto[] {
  if (!Array.isArray(rows) || !rows.length) return [];
  return rows.flatMap((row) => {
    if (!row?.enabled) return [];
    const values: { attr_code: string; value: string[] }[] = [];
    for (const attr of row?.attributes ?? []) {
      const attrValue = attrValueFromFormValue(attr, row?.[attr.attr_code]);
      if (attrValue)
        values.push({ attr_code: attr.attr_code, value: attrValue });
    }
    if (!values.length) return [];
    return [
      {
        benefit_code: row.group_value,
        values,
        template:
          row.template ??
          serializeBenefitTemplate(row as unknown as BenefitGroupDef),
      },
    ];
  });
}
